/**
 * RenderTarget.ts — shared offscreen color + depth buffer for the whole frame.
 *
 * Color: rgba16float (HDR). Depth: depth32float, REVERSE-Z.
 *
 * Reverse-Z conventions used everywhere in the codebase:
 *   - Projection matrices map near→1, far→0.
 *   - Pipelines use `depthCompare: 'greater-equal'`.
 *   - Render passes clear depth to `0.0` (NOT 1.0).
 *
 * Recreated automatically on canvas resize.
 *
 * No pass except `BlitPass` may write to canvas.getCurrentTexture(); every
 * other pass (Sky, Terrain, Water, FFT debug, ...) renders into this target.
 */

export interface RenderTarget {
  color: GPUTexture;
  colorView: GPUTextureView;
  depth: GPUTexture;
  depthView: GPUTextureView;
  width: number;
  height: number;
  readonly colorFormat: GPUTextureFormat;
  readonly depthFormat: GPUTextureFormat;
  readonly sampleCount: number;
}

export const COLOR_FORMAT: GPUTextureFormat = "rgba16float";
export const DEPTH_FORMAT: GPUTextureFormat = "depth32float";
export const SAMPLE_COUNT = 1;
/** Reverse-Z clear depth. 0.0 corresponds to "infinitely far". */
export const REVERSE_Z_CLEAR_DEPTH = 0.0;
/** Reverse-Z depth compare. */
export const REVERSE_Z_COMPARE: GPUCompareFunction = "greater-equal";

export function createRenderTarget(
  device: GPUDevice,
  width: number,
  height: number,
): RenderTarget {
  const color = device.createTexture({
    label: "RT.color (rgba16float)",
    size: { width, height },
    format: COLOR_FORMAT,
    usage:
      GPUTextureUsage.RENDER_ATTACHMENT |
      GPUTextureUsage.TEXTURE_BINDING |
      GPUTextureUsage.COPY_DST |
      GPUTextureUsage.COPY_SRC,
    sampleCount: SAMPLE_COUNT,
  });
  const depth = device.createTexture({
    label: "RT.depth (depth32float, reverse-Z)",
    size: { width, height },
    format: DEPTH_FORMAT,
    usage:
      GPUTextureUsage.RENDER_ATTACHMENT |
      GPUTextureUsage.TEXTURE_BINDING |
      GPUTextureUsage.COPY_DST |
      GPUTextureUsage.COPY_SRC,
    sampleCount: SAMPLE_COUNT,
  });
  return {
    color,
    colorView: color.createView({ label: "RT.color view" }),
    depth,
    depthView: depth.createView({ label: "RT.depth view" }),
    width,
    height,
    colorFormat: COLOR_FORMAT,
    depthFormat: DEPTH_FORMAT,
    sampleCount: SAMPLE_COUNT,
  };
}

export function destroyRenderTarget(rt: RenderTarget): void {
  rt.color.destroy();
  rt.depth.destroy();
}

/**
 * Manages the lifetime of a single RenderTarget across resize events.
 * Notifies subscribers (typically: passes that bind the RT as a texture)
 * with the freshly created RT so they can rebuild bind groups.
 */
export class ManagedRenderTarget {
  private _rt: RenderTarget;
  private listeners = new Set<(rt: RenderTarget) => void>();

  constructor(
    private device: GPUDevice,
    width: number,
    height: number,
  ) {
    this._rt = createRenderTarget(device, width, height);
  }

  get current(): RenderTarget {
    return this._rt;
  }

  resize(width: number, height: number): void {
    if (width === this._rt.width && height === this._rt.height) return;
    destroyRenderTarget(this._rt);
    this._rt = createRenderTarget(this.device, width, height);
    for (const cb of this.listeners) cb(this._rt);
  }

  /** Subscribe to RT recreation. Returns an unsubscribe function. */
  onRecreate(cb: (rt: RenderTarget) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  destroy(): void {
    destroyRenderTarget(this._rt);
    this.listeners.clear();
  }
}
